import React from 'react';
import { Link } from 'react-router-dom';
import { Globe, Users, Calendar, Share2, ArrowUpRight, Orbit, Image as ImageIcon } from 'lucide-react';
import HeroCarousel from '../../components/HeroCarousel';

// ARCHETYPE 4: The Cosmic Observatory — Matrix grid, futuristic, neon glow
// Unique: Orbitron/Roboto Mono, grid overlay, section names (Scan, Data, Crew, Log, Captures, Transmissions)
const ObservatoryTemplate = ({ association: a, activities, theme: t, onShare }) => {
  const C = {
    bg: t.bg || '#0a0a0f',
    primary: t.primary || '#020208',
    text: '#FFFFFF',
    accent: t.secondary || '#00E5FF',
  };

  return (
    <div style={{ backgroundColor: C.bg, color: C.text, fontFamily: "'Roboto Mono', monospace" }} className="min-h-screen">
      <nav className="fixed top-0 left-0 right-0 z-50 border-b" style={{ backgroundColor: `${C.bg}ee`, borderColor: `${C.accent}15` }} data-testid="association-nav">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-12">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full border flex items-center justify-center" style={{ borderColor: C.accent, boxShadow: `0 0 8px ${C.accent}40` }}>
              <img src={a.logo_url||a.image_url} alt="" className="w-7 h-7 rounded-full object-cover" />
            </div>
            <span className="text-sm font-bold" style={{ fontFamily: "'Orbitron', sans-serif", color: C.accent }}>{a.short_name}</span>
          </div>
          <div className="hidden md:flex gap-1">
            {['Scan','Data','Crew','Log','Capture'].map(s => (
              <a key={s} href={`#${s.toLowerCase()}`} className="px-3 py-1 text-[10px] font-bold uppercase tracking-widest" style={{ color: `${C.accent}66` }}>{s}</a>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onShare} className="p-1.5" data-testid="share-btn"><Share2 className="w-3.5 h-3.5" style={{ color: C.accent }} /></button>
            <Link to="/" className="text-[10px] uppercase tracking-widest px-2 py-1 border" style={{ borderColor: `${C.accent}30`, color: `${C.accent}88` }} data-testid="cca-return-badge">CCA Base</Link>
          </div>
        </div>
      </nav>

      {/* Hero — Full-screen scan with visible cultural photo */}
      <section id="scan" className="min-h-screen flex items-center justify-center pt-12 relative" data-testid="association-hero">
        <div className="absolute inset-0">
          <HeroCarousel
            images={a.hero_images?.length ? a.hero_images : []}
            overlay={`radial-gradient(ellipse at 50% 50%, ${C.accent}08, ${C.bg}cc)`}
            className="w-full h-full"
          />
        </div>
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: `linear-gradient(${C.accent}20 1px,transparent 1px),linear-gradient(90deg,${C.accent}20 1px,transparent 1px)`, backgroundSize: '80px 80px' }} />
        <div className="relative text-center px-4">
          <Orbit className="w-10 h-10 mx-auto mb-4" style={{ color: C.accent }} />
          <h1 className="text-6xl sm:text-7xl lg:text-8xl font-black tracking-tighter" style={{ fontFamily: "'Orbitron', sans-serif", color: C.accent, textShadow: `0 0 60px ${C.accent}25` }} data-testid="association-name">{a.short_name}</h1>
          <p className="text-sm mt-4 opacity-40 uppercase tracking-widest">{a.name}</p>
          {a.cultural_highlights && (
            <p className="mt-8 max-w-lg mx-auto text-xs leading-relaxed opacity-50 border p-4" style={{ borderColor: `${C.accent}15` }} data-testid="cultural-highlights-banner">{a.cultural_highlights}</p>
          )}
        </div>
      </section>

      {/* Data // About */}
      <section id="data" className="py-24 px-6 border-t" style={{ borderColor: `${C.accent}10` }}>
        <div className="max-w-5xl mx-auto grid md:grid-cols-12 gap-4">
          <div className="md:col-span-8 p-6 border" style={{ borderColor: `${C.accent}12` }}>
            <span className="text-[10px] uppercase tracking-widest block mb-3" style={{ color: C.accent }}>Data // About</span>
            <p className="leading-relaxed opacity-60" data-testid="association-description">{a.description}</p>
            {a.origin && <p className="mt-4 text-xs opacity-30 flex items-center gap-2"><Globe className="w-3 h-3" />{a.origin}</p>}
          </div>
          <div className="md:col-span-4 space-y-4">
            <div className="p-4 border" style={{ borderColor: `${C.accent}12` }}>
              <span className="text-[10px] uppercase tracking-widest block mb-2" style={{ color: C.accent }}>Mission</span>
              <p className="text-xs opacity-50" data-testid="association-mission">{a.mission}</p>
            </div>
            <div className="p-4 border" style={{ borderColor: `${C.accent}12` }}>
              <span className="text-[10px] uppercase tracking-widest block mb-2" style={{ color: C.accent }}>Values</span>
              <div className="flex flex-wrap gap-1" data-testid="association-values">
                {(a.values||[]).map((v,i) => <span key={i} className="px-2 py-0.5 text-[10px] border" style={{ borderColor: `${C.accent}20`, color: C.accent }}>{v}</span>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Heritage Scan */}
      <section className="py-24 px-6" style={{ backgroundColor: `${C.accent}04` }}>
        <div className="max-w-4xl mx-auto">
          <span className="text-[10px] uppercase tracking-widest block mb-3" style={{ color: C.accent }}>Heritage Scan</span>
          <p className="text-sm leading-relaxed opacity-50" data-testid="cultural-background">{a.cultural_background||a.cultural_highlights}</p>
        </div>
      </section>

      {/* Crew */}
      <section id="crew" className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-lg font-bold mb-8" style={{ fontFamily: "'Orbitron', sans-serif", color: C.accent }}>CREW</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="border p-5 text-center" style={{ borderColor: `${C.accent}20` }}>
              <div className="w-16 h-16 rounded-full mx-auto mb-3 border overflow-hidden" style={{ borderColor: C.accent }}>
                {a.president_photo_url
                  ? <img src={a.president_photo_url} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: `${C.accent}10` }}>
                      <span className="text-xl font-bold" style={{ color: C.accent }}>{(a.president_name||'?')[0]}</span>
                    </div>
                }
              </div>
              <h4 className="text-sm font-bold">{a.president_name}</h4>
              <p className="text-[10px]" style={{ color: C.accent }}>Commander</p>
            </div>
            {(a.leadership||[]).map((l,i) => (
              <div key={l.id||i} className="border p-5 text-center" style={{ borderColor: `${C.accent}10` }}>
                <div className="w-16 h-16 rounded-full mx-auto mb-3 bg-white/5 overflow-hidden">
                  {l.photo_url
                    ? <img src={l.photo_url} alt="" className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center"><span className="text-xl opacity-20">{(l.name||'?')[0]}</span></div>
                  }
                </div>
                <h4 className="text-sm font-bold">{l.name}</h4>
                <p className="text-[10px] opacity-40">{l.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Event Log */}
      <section id="log" className="py-24 px-6" style={{ backgroundColor: `${C.accent}04` }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-lg font-bold mb-6" style={{ fontFamily: "'Orbitron', sans-serif", color: C.accent }}>EVENT LOG</h2>
          {activities.length > 0 ? (
            <div className="space-y-2 font-mono text-xs">
              {activities.map(ev => (
                <div key={ev.id} className="flex gap-4 border-b py-3" style={{ borderColor: `${C.accent}08` }} data-testid={`event-${ev.id}`}>
                  <span style={{ color: C.accent }}>[{ev.date}]</span>
                  <span className="opacity-60">{ev.title}</span>
                </div>
              ))}
            </div>
          ) : <p className="opacity-30 text-sm">Log empty — no events scheduled.</p>}
        </div>
      </section>

      {/* Captures — Gallery */}
      <section id="capture" className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-lg font-bold mb-6" style={{ fontFamily: "'Orbitron', sans-serif", color: C.accent }}>CAPTURES</h2>
          {a.gallery_images?.length > 0 ? (
            <div className="columns-2 md:columns-3 gap-3 space-y-3">
              {a.gallery_images.map((img,i) => (
                <div key={i} className="break-inside-avoid border" style={{ borderColor: `${C.accent}10` }} data-testid={`gallery-image-${i}`}>
                  <img src={img.url} alt="" className="w-full object-cover" loading="lazy" />
                </div>
              ))}
            </div>
          ) : <p className="opacity-30 text-sm">No captures yet. Gallery coming soon.</p>}
        </div>
      </section>

      {/* Transmissions — Videos */}
      <section className="py-24 px-6" style={{ backgroundColor: `${C.accent}04` }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-lg font-bold mb-6" style={{ fontFamily: "'Orbitron', sans-serif", color: C.accent }}>TRANSMISSIONS</h2>
          {(a.youtube_videos||[]).length > 0 ? (
            <div className="grid md:grid-cols-2 gap-4">
              {a.youtube_videos.map((v,i) => (
                <div key={i} className="border" style={{ borderColor: `${C.accent}10` }} data-testid={`video-${i}`}>
                  <div className="aspect-video"><iframe src={`https://www.youtube.com/embed/${v.video_id}`} title={v.title||''} className="w-full h-full" allowFullScreen /></div>
                </div>
              ))}
            </div>
          ) : <p className="opacity-30 text-sm">No transmissions yet. Videos will appear here.</p>}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 text-center border-t" style={{ borderColor: `${C.accent}10` }}>
        <img src={a.logo_url || a.image_url} alt="" className="w-12 h-12 rounded-full mx-auto mb-4 object-cover border" style={{ borderColor: C.accent, boxShadow: `0 0 12px ${C.accent}20` }} />
        <h3 className="text-xl font-bold" style={{ fontFamily: "'Orbitron', sans-serif", color: C.accent }}>{a.short_name}</h3>
        <p className="text-xs opacity-30 mt-2 mb-6">{a.name}</p>
        <div className="flex justify-center gap-6 text-[10px] opacity-30 uppercase tracking-widest">
          <Link to="/" className="hover:opacity-100">CCA Base <ArrowUpRight className="w-3 h-3 inline" /></Link>
          <Link to="/associations" className="hover:opacity-100">Directory</Link>
        </div>
      </footer>
    </div>
  );
};
export default ObservatoryTemplate;
